<template>
  <CommonModal ref="commonModal" compact hide-close-button bottom-aligned @on-close="onModalClose">
    <div class="size-chart-modal">
      <div class="size-chart-modal__header-container">
        <div class="size-chart-modal__header">Numbers Size Guide</div>
        <button
          v-if="!hideCloseButton"
          class="size-chart-modal__modal-button-close"
          type="button"
          @click="closeModal"
        >
          <UseSvg class="size-chart-modal__modal-button-close-icon" icon="menu-close" />
        </button>
      </div>
      <div class="size-chart-modal__content">
        <div class="size-chart-modal__size-container size-chart-modal__size-container--label">
          <h2 class="size-chart-modal__size"></h2>
          <div
            v-for="product in products"
            :key="product"
            class="size-chart-modal__product"
            :class="[`size-chart-modal__product--${product.id}`]"
          >
            <div class="size-chart-modal__product-name" s>
              {{ product.name }}
            </div>
          </div>
        </div>
        <div
          v-for="(size, index) in sizes"
          :key="size.size"
          class="size-chart-modal__size-container"
          :class="`size-chart-modal__size-container--${size.size.slice(0, -1)}`"
        >
          <h2 class="size-chart-modal__size size-chart-modal__size--pill">
            {{ size.size }}
          </h2>
          <div
            v-for="product in products"
            :key="product"
            class="size-chart-modal__product"
            :class="[`size-chart-modal__product--${product.id}`]"
          >
            <div
              class="size-chart-modal__product-name"
              :class="{
                hidden: index > 0,
                'size-chart-modal__product-name--inline': index === 0
              }"
            >
              {{ product.name }}
            </div>
            <div
              class="size-chart-modal__image"
              :class="[`size-chart-modal__image--${size.size.slice(0, -1)}`]"
            />
            <div v-if="size.recommended === product.id" class="size-chart-modal__image-tag">
              RECOMMENDED
            </div>
          </div>
        </div>
      </div>
    </div>
  </CommonModal>
</template>

<script>
import UseSvg from "common/vue/components/UseSvg.vue";
import CommonModal from "common/vue/components/CommonModal.vue";

export default {
  name: "SizeChartModal",
  components: { CommonModal, UseSvg },
  emits: ["on-close"],
  data() {
    return {
      sizes: [
        { size: '10"', recommended: "adult-jersey" },
        { size: '8"', recommended: "adult" },
        { size: '6"', recommended: "youth" },
        { size: '4"', recommended: null }
      ],
      products: [
        { id: "youth", name: "Youth Sizes" },
        { id: "adult", name: "Adult Sizes" },
        { id: "adult-jersey", name: "Adult Jersey Sizes" }
      ]
    };
  },
  methods: {
    closeModal() {
      this.$refs.commonModal.close();
    },
    onModalClose() {
      this.$emit("on-close");
    }
  }
};
</script>
<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

@function get-image-url($image) {
  @return url(remote-image("gen/size-chart-#{$image}.png", "ixlib=rb-0.3.5&dpr=2&q=30"));
}

.size-chart-modal {
  $this: &;
  display: flex;
  flex-direction: column;
  padding: 0;
  width: 100vw;

  @include with-tablet {
    padding: 15px;
    width: auto;
  }

  &__header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 15px;

    @include with-tablet {
      margin: 15px 0;
    }
  }

  &__header {
    font-family: $font-family-secondary;
    font-size: 16px;
    font-weight: 700;
    line-height: 18px;
    text-align: left;
  }

  &__modal-button-close {
    padding: 0;
    color: $text-black;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
    background-color: $white;
    border: none;
  }

  &__modal-button-close-icon {
    width: 32px;
    height: 32px;
  }

  &__content {
    display: flex;
    overflow: hidden;

    @include with-tablet {
      width: 520px;
    }

    @include with-desktop {
      width: 650px;
    }
  }

  &__size-container {
    display: flex;
    flex-direction: column;
    width: 85px;
    flex-basis: 25%;

    @include with-tablet {
      width: 125px;
    }

    &--label {
      display: none;

      @include with-desktop {
        display: block;
      }
    }

    &--10 {
      #{$this}__size {
        right: -20px;
      }
    }

    &--8 {
      #{$this}__size {
        right: -5px;
      }
    }

    &--6 {
      #{$this}__size {
        right: 10px;
      }
    }

    &--4 {
      #{$this}__size {
        right: 25px;
      }
    }
  }

  &__size {
    font-family: $font-family-secondary;
    font-size: 16px;
    font-weight: 700;
    line-height: 14px;
    text-align: left;
    width: 62px;
    height: 38px;
    padding: 12px 16px;
    gap: 8px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;

    &--pill {
      background-color: $lavender-darker;
      position: relative;
    }
  }

  &__product {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    height: 160px;
    padding-bottom: 15px;

    @include with-tablet {
      height: 190px;
    }

    @include with-desktop {
      justify-content: center;
      padding-bottom: 0;
    }

    &--youth {
      height: 145px;
      background-color: $white-ghost;

      @include with-tablet {
        height: 160px;
      }

      @include with-desktop {
        height: 130px;
      }

      #{$this}__image {
        height: 100px;
        width: 80px;
        background-image: get-image-url("artwork"), get-image-url("youth-tshirt");

        @include with-tablet {
          height: 108px;
          width: 85px;
        }

        &--10 {
          right: -20px;
          z-index: 4;
          background-size: 55%, contain;
          background-position-y: 25%, center;
        }

        &--8 {
          right: -5px;
          z-index: 3;
          background-size: 52%, contain;
          background-position-y: 25%, center;
        }

        &--6 {
          right: 10px;
          z-index: 2;
          background-size: 40%, contain;
          background-position-y: 30%, center;
        }

        &--4 {
          right: 25px;
          z-index: 1;
          background-size: 35%, contain;
          background-position-y: 30%, center;
        }
      }

      #{$this}__image-tag {
        bottom: 17%;
        right: 50%;
        transform: translate(40%);

        @include with-tablet {
          right: 24px;
          transform: none;
        }
      }
    }

    &--adult {
      background-color: $lavender-light;

      #{$this}__image {
        height: 115px;
        width: 100px;
        background-image: get-image-url("artwork"), get-image-url("adult-tshirt");

        @include with-tablet {
          height: 137px;
          width: 118px;
        }

        &--10 {
          z-index: 4;
          right: -20px;
          background-size: 43%, contain;
          background-position-y: 21%, center;
        }

        &--8 {
          z-index: 3;
          right: -5px;
          background-size: 40%, contain;
          background-position-y: 22%, center;
        }

        &--6 {
          z-index: 2;
          right: 10px;
          background-size: 32%, contain;
          background-position-y: 25%, center;
        }

        &--4 {
          z-index: 1;
          right: 25px;
          background-size: 30%, contain;
          background-position-y: 25%, center;
        }
      }

      #{$this}__image-tag {
        bottom: 15%;
        right: 50%;
        transform: translate(56%);

        @include with-tablet {
          right: 7px;
          transform: none;
        }

        @include with-desktop {
          bottom: 19%;
          transform: none;
        }
      }
    }

    &--adult-jersey {
      background-color: $lavender-dark;
      padding: 0;

      @include with-tablet {
        height: 180px;
      }

      #{$this}__image {
        height: 138px;
        width: 128px;
        background-image: get-image-url("artwork"), get-image-url("adult-jersey-tshirt");

        @include with-tablet {
          right: auto;
          height: 156px;
          width: 156px;
        }

        &--10 {
          z-index: 4;
          right: -20px;
          background-size: 33%, contain;
          background-position-y: 45%, center;
        }

        &--8 {
          z-index: 3;
          right: -7px;
          background-size: 31%, contain;
          background-position-y: 45%, center;
        }

        &--6 {
          z-index: 2;
          right: 7px;
          background-size: 25%, contain;
          background-position-y: 45%, center;
        }

        &--4 {
          z-index: 1;
          right: 20px;
          background-size: 22%, contain;
          background-position-y: 45%, center;
        }
      }

      #{$this}__image-tag {
        bottom: 15%;
        right: 50%;
        transform: translate(70%);

        @include with-tablet {
          right: -3px;
          transform: none;
        }

        @include with-desktop {
          bottom: 19%;
          transform: none;
        }
      }
    }
  }

  &__product-name {
    font-family: $font-family-secondary;
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    text-align: left;
    width: 85px;

    &--inline {
      position: absolute;
      top: 10px;
      left: 15px;
      width: 125px;
      z-index: 1;

      @include with-desktop {
        display: none;
      }
    }
  }

  &__image {
    background-size: contain;
    background-repeat: no-repeat;
    background-position-x: center;
    position: relative;
  }

  &__image-tag {
    height: 20px;
    padding: 4px 6px;
    border-radius: 4px;
    position: absolute;
    color: $white;
    font-family: $font-family-secondary;
    font-size: 11px;
    font-weight: 700;
    line-height: 12px;
    text-align: center;
    background-color: $badge-purple;
    z-index: 5;
    bottom: 20%;
  }
}
</style>
